import { ApiError, apiFetch } from './client';
export async function getStudyList(query = {}, options = {}) {
    const params = new URLSearchParams();
    if (query.search)
        params.set('search', query.search);
    if (query.referringPhysiciansName)
        params.set('referringPhysiciansName', query.referringPhysiciansName);
    if (query.reportKeyword)
        params.set('reportKeyword', query.reportKeyword);
    if (query.reportSection)
        params.set('reportSection', query.reportSection);
    if (query.tag)
        params.set('tag', query.tag);
    if (query.patientName)
        params.set('patientName', query.patientName);
    if (query.search)
        params.set('search', query.search);
    if (query.ownerName)
        params.set('ownerName', query.ownerName);
    if (query.species)
        params.set('species', query.species);
    if (query.description)
        params.set('description', query.description);
    if (query.fromDateTime)
        params.set('fromDateTime', query.fromDateTime);
    if (query.toDateTime)
        params.set('toDateTime', query.toDateTime);
    if (query.modality)
        params.set('modality', query.modality);
    if (query.accessionNumber)
        params.set('accessionNumber', query.accessionNumber);
    if (query.clinicCode)
        params.set('clinicCode', query.clinicCode);
    if (query.institutionName)
        params.set('institutionName', query.institutionName);
    if (query.sortBy)
        params.set('sortBy', query.sortBy);
    if (query.sortDirection)
        params.set('sortDirection', query.sortDirection);
    if (query.page)
        params.set('page', query.page.toString());
    if (query.pageSize)
        params.set('pageSize', query.pageSize.toString());
    if (query.folderId)
        params.set('folderId', query.folderId);
    if (query.favouritesOnly)
        params.set('favouritesOnly', 'true');
    const queryString = params.toString();
    const endpoint = `/study/list${queryString ? `?${queryString}` : ''}`;
    return apiFetch(endpoint, { signal: options.signal });
}
/**
 * Type-ahead suggestions across patient name, MRN (DICOM patient id), accession
 * number, and study description. Clinic-scoped server-side like the worklist.
 */
export async function getStudySuggest(q, limit = 8) {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return apiFetch(`/study/suggest?${params.toString()}`);
}
export async function getStudy(id, caseId) {
    const qs = caseId ? `?caseId=${encodeURIComponent(caseId)}` : '';
    return apiFetch(`/study/${encodeURIComponent(id)}${qs}`);
}
/**
 * Study detail WITHOUT the instance rows: same study-level fields as
 * {@link getStudy}, but one summary per series instead of every image. This is
 * what the viewer opens on, so a 10k-instance study costs O(series) to paint;
 * the instances arrive per series via {@link getSeriesInstances}.
 *
 * Non-viewer surfaces (download, DICOM send, consultation submit, the Link
 * preview strip) deliberately stay on {@link getStudy} — they need every
 * instance up front anyway, so paging would only add round trips.
 */
export async function getStudySummary(id, caseId) {
    const qs = caseId ? `?caseId=${encodeURIComponent(caseId)}` : '';
    return apiFetch(`/study/${encodeURIComponent(id)}/summary${qs}`);
}
/**
 * One page of a series' instances. `page` is 1-based.
 *
 * `intent` is omitted from the request entirely unless the caller passes it,
 * so today's behaviour (the server's `demand` default) is byte-for-byte
 * unchanged for every existing caller. Wiring which callers pass `background`
 * is a later change — this is only the plumbing.
 */
export async function getSeriesInstances(studyId, seriesId, options = {}) {
    const params = new URLSearchParams();
    if (options.page != null)
        params.set('page', String(options.page));
    if (options.pageSize != null)
        params.set('pageSize', String(options.pageSize));
    if (options.caseId)
        params.set('caseId', options.caseId);
    if (options.intent != null)
        params.set('intent', options.intent);
    const queryString = params.toString();
    return apiFetch(`/study/${encodeURIComponent(studyId)}/series/${encodeURIComponent(seriesId)}/instances${queryString ? `?${queryString}` : ''}`);
}
export async function getRelatedStudies(studyId, limit = 12) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/related?limit=${encodeURIComponent(String(limit))}`);
}
/** Same-clinic, same-patient-id studies that may be folded into this one, with warnings and blockers. */
export async function getMergeCandidates(studyId) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/merge-candidates`);
}
/**
 * Fold the given studies into `targetId`: their series move under it and they are removed.
 * A refusal is a 409 whose body is a `MergeStudiesConflict`.
 */
export async function mergeStudies(targetId, sourceStudyIds) {
    return apiFetch(`/study/${encodeURIComponent(targetId)}/merge`, {
        method: 'POST',
        body: JSON.stringify({ sourceStudyIds }),
    });
}
/** Request a de-identified copy of a study; the copy is produced in the background. */
export async function anonymizeStudy(studyId, body) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/anonymize`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}
export async function getAnonymizeJob(jobId) {
    return apiFetch(`/study/anonymize-jobs/${encodeURIComponent(jobId)}`);
}
/** Non-draft standard + teleconsultation reports attachable to a study-share email. */
export async function getStudyReports(studyId) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/reports`);
}
function mapStudyToDicomFlat(study) {
    const p = study.patient;
    const s = study.study;
    return {
        patientName: p.name ?? undefined,
        patientId: p.id ?? undefined,
        responsiblePerson: p.ownerName ?? undefined,
        patientSpecies: p.species ?? undefined,
        patientSex: p.sex ?? undefined,
        patientWeight: p.weight ?? undefined,
        modalityType: s.modality ?? undefined,
        requestedProcedureDescription: s.description ?? undefined,
        referringPhysician: study.referringPhysiciansName ?? undefined,
        accessionNumber: s.accessionNumber ?? undefined,
    };
}
export async function getStudyDicomMetadata(studyId) {
    try {
        return await apiFetch(`/studies/${encodeURIComponent(studyId)}/dicom-metadata`);
    }
    catch (e) {
        if (e instanceof ApiError && e.status !== 404)
            return null;
    }
    try {
        const study = await getStudy(studyId);
        return mapStudyToDicomFlat(study);
    }
    catch {
        return null;
    }
}
/**
 * Share a study by email: the backend mints a secure share link (public
 * SharedViewer) and emails it to the recipients. Authorized + recipient-capped
 * + audited server-side.
 */
export async function shareStudyEmail(studyId, payload) {
    // The endpoint mints the share + dispatches the emails fire-and-forget, so it
    // returns the recipient count it accepted (not a delivered count).
    return apiFetch(`/study/${encodeURIComponent(studyId)}/share-email`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}
// Delete API
export async function deleteStudy(id) {
    return apiFetch(`/study/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}
export async function updateStudy(id, patch) {
    return apiFetch(`/study/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
}
export async function deleteSeries(seriesIds) {
    return apiFetch('/study/series', {
        method: 'DELETE',
        body: JSON.stringify({ seriesIds }),
    });
}
export async function deleteImages(imageIds) {
    return apiFetch('/study/images', {
        method: 'DELETE',
        body: JSON.stringify({ imageIds }),
    });
}
// Study Clinics API (for study filtering dropdown)
export async function getStudyClinics() {
    return apiFetch('/study/clinics');
}
// Study Read Status API
export async function markStudiesRead(studyIds) {
    return apiFetch('/study/read', {
        method: 'POST',
        body: JSON.stringify({ studyIds }),
    });
}
export async function getUnreadCount() {
    return apiFetch('/study/unread-count');
}
export async function getFavouritesCount() {
    return apiFetch('/study/favourites-count');
}
export async function getStudyBookmarks(studyId) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/bookmarks`);
}
export async function addStudyBookmark(studyId, bookmark) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/bookmarks`, {
        method: 'POST',
        body: JSON.stringify(bookmark),
    });
}
export async function removeStudyBookmark(studyId, bookmark) {
    return apiFetch(`/study/${encodeURIComponent(studyId)}/bookmarks/${encodeURIComponent(bookmark.seriesId)}/${bookmark.imageIndex}`, { method: 'DELETE' });
}
export async function requestImportUploadUrls(clinicId, files, signal, nonDicomMetadata, importSessionId) {
    const res = await apiFetch('/study/import/sign', {
        method: 'POST',
        body: JSON.stringify({ clinicId, files, nonDicomMetadata, importSessionId }),
        signal,
    });
    return res.files;
}
/**
 * Marks a batch of already-uploaded objects as ready for asynchronous parsing.
 * Replaces the old synchronous `finalizeImport`: the server no longer parses
 * inline — it enqueues the uploads for the out-of-process ImportWorker and
 * returns how many it queued. Callers then poll {@link getImportStatus} until
 * each upload settles.
 */
export async function completeImport(clinicId, uploadIds, signal) {
    return apiFetch('/study/import/complete', {
        method: 'POST',
        body: JSON.stringify({ clinicId, uploadIds }),
        signal,
    });
}
/**
 * Polls the async parse state for a batch of uploads. Sends the clinic id plus a
 * repeated `ids` query param per upload, and returns the per-upload states.
 */
export async function getImportStatus(clinicId, uploadIds, signal) {
    const params = new URLSearchParams();
    params.set('clinicId', clinicId);
    for (const id of uploadIds)
        params.append('ids', id);
    const res = await apiFetch(`/study/import/status?${params.toString()}`, { signal });
    return res.files;
}
export async function getStudyIngestStatus(id) {
    return apiFetch(`/study/${encodeURIComponent(id)}/ingest-status`);
}
